/**
 * One imported clip on a reel page. The object is plain canvas JSON so the
 * existing renderer can load it. No Fabric import: unit tests stay node-only.
 */

import type { ProjectPage, VideoClipRef } from '@canvas/shared-types';
import { REEL_CAPTION_KEY } from './reelCaption';
import { clampClip, serializableClipRef, VIDEO_OBJECT_TYPE } from '@/lib/video/videoClip';

/** Cover-fit a clip to the page and center it. Wide footage is cropped. */
export function reelClipFrame(
  clip: Pick<VideoClipRef, 'naturalWidth' | 'naturalHeight'>,
  pageWidth: number,
  pageHeight: number
): { left: number; top: number; scale: number } {
  const width = Math.max(1, clip.naturalWidth || 1);
  const height = Math.max(1, clip.naturalHeight || 1);
  const scale = Math.max(pageWidth / width, pageHeight / height);
  return { left: pageWidth / 2, top: pageHeight / 2, scale };
}

function isCaption(obj: Record<string, unknown>): boolean {
  return obj[REEL_CAPTION_KEY] === true;
}

function isClip(obj: Record<string, unknown>): boolean {
  return obj.type === VIDEO_OBJECT_TYPE;
}

/** Stored video object. The poster is resolved from the clip ref, not from src. */
export function reelClipObject(
  clip: VideoClipRef,
  pageWidth: number,
  pageHeight: number
): Record<string, unknown> {
  const frame = reelClipFrame(clip, pageWidth, pageHeight);
  return {
    type: VIDEO_OBJECT_TYPE,
    originX: 'center',
    originY: 'center',
    left: frame.left,
    top: frame.top,
    width: clip.naturalWidth,
    height: clip.naturalHeight,
    scaleX: frame.scale,
    scaleY: frame.scale,
    angle: 0,
    opacity: 1,
    src: '',
    filters: [],
    objectCaching: false,
    selectable: false,
    evented: false,
    craftaraVideo: serializableClipRef(clampClip(clip)),
  };
}

/** Replace any clip on the page. The caption stays in front. */
export function pageWithReelClip(page: ProjectPage, clip: VideoClipRef): ProjectPage {
  const width = page.canvasData.width || 1080;
  const height = page.canvasData.height || 1920;
  const kept = (page.canvasData.objects || []).filter((obj) => !isClip(obj));
  const video = reelClipObject(clip, width, height);
  const captionAt = kept.findIndex(isCaption);
  const objects = captionAt >= 0
    ? [...kept.slice(0, captionAt), video, ...kept.slice(captionAt)]
    : [...kept, video];
  return { ...page, canvasData: { ...page.canvasData, objects } };
}

/**
 * True when the stage should seek the clip. Play and scrub do.
 * Stopped at the start of the scene keeps the poster.
 */
export function reelStageSeeksClip(playback: string, localMs: number): boolean {
  return playback !== 'stopped' || localMs > 40;
}

/** Patch the clip ref. Geometry and the caption stay. */
export function patchReelClip(page: ProjectPage, patch: Partial<VideoClipRef>): ProjectPage {
  let changed = false;
  const objects = (page.canvasData.objects || []).map((obj) => {
    if (!isClip(obj) || !obj.craftaraVideo || typeof obj.craftaraVideo !== 'object') return obj;
    changed = true;
    const next = serializableClipRef(
      clampClip({ ...(obj.craftaraVideo as VideoClipRef), ...patch })
    );
    return { ...obj, craftaraVideo: next };
  });
  if (!changed) return page;
  return { ...page, canvasData: { ...page.canvasData, objects } };
}
